<script setup lang="ts">
import { IMAGE_FIT } from '@pkg/sanity-utils/constants/imageFit';
import type { Image } from '@sanity/types';
import type { ImageOptions } from '@pkg/ui/composables/useSanityImage';
import { useSanityImage } from '@pkg/ui/composables/useSanityImage';

/**
 * [1] Alt text may be defined on the image itself.
 * [2] Alt text can be passed in to override what's on the image, or as a fallback in case nothing is defined on the image.
 * [3] Above the fold images should be 'eager'. All else should be 'lazy'. Defaults to 'lazy'.
 * [4] Specify a custom 'sizes' attribute for this image. Only used if props.maxWidth or props.width are also set.
 */
const props = defineProps<{
  image?: Image & { alt?: string, fit?: IMAGE_FIT } /* [1] */ | null;
  height?: number;
  width?: number;
  alt?: string; /* [2] */
  altFallback?: string; /* [2] */
  loading?: 'lazy' | 'eager'; /* [3] */
  imageOptions?: ImageOptions;
  sizes?: string; /* [4] */
}>();
const altText = computed(() => props.alt || props.image?.alt || props.altFallback || '');

const { src, srcset, sizes, height, width } = useSanityImage(
  props.image,
  { maxWidth: props.width || undefined, sizes: props.sizes || undefined },
  {
    width: props.width,
    height: props.height,
    ...props.imageOptions,
  },
);

if (props.loading === 'eager' && src.value) {
  useHead({
    link: [{
      rel: 'preload',
      as: 'image',
      href: src.value,
      imagesrcset: srcset.value,
      imagesizes: sizes.value,
      fetchpriority: 'high',
    }],
  });
}

// @todo add blurhash if present in image
</script>

<template>
  <img
    v-if="src"
    :src="src"
    :srcset="srcset"
    :sizes="sizes"
    :alt="altText"
    :width="width"
    :height="height"
    :loading="props.loading || 'lazy'"
    :fetchpriority="props.loading === 'eager' ? 'high' : undefined"
    :class="['c-base-image', {
      'object-cover': props.image?.fit == null || props.image?.fit === IMAGE_FIT.COVER,
      'object-contain': props.image?.fit === IMAGE_FIT.CONTAIN,
    }]"
  >
</template>

<style scoped>
img {
  height: auto;
}
</style>
